/**
 * bodyMotionClip — turn a kimodo tracks payload into a first-class mixer clip and
 * play it through the SAME one-shot path prefabs/gestures use (__DIRECT_PLAY__),
 * so the generated motion registers in window.__AVATAR_ACTIONS__ and animates
 * (the surface ENG-705 asserts) rather than living in a bespoke post-mixer
 * override.
 *
 * It bakes the root-bone up-axis correction (cancel the rotation the rig nests
 * above the pelvis) and the world-meter->pelvis-local root translation directly
 * into keyframes, so the mixer can drive it as an ordinary AnimationAction.
 */
import * as THREE from 'three'
import { getLiveBodyRig } from './liveBodyRig.js'
import { resolveAvatarTransformTarget } from './avatarTransformTarget.js'
import { createRootMotion, hasTravel } from './generatedRootMotion.js'

const ROOT_BONE = 'pelvis'

/**
 * Build a THREE.AnimationClip bound to the live avatar skeleton from a kimodo
 * tracks payload.
 * @param {object} tracks { fps, frames, bones:{name:[x,y,z,w,...]}, root?:[x,y,z,...] }
 * @param {object} [opts] { scene, name, rootMode }
 *   rootMode: 'travel' (default) builds the pelvis position track so the clip
 *     locomotes (walk/jump leave the ground) — the anchor/navmesh path.
 *     'lock' skips the pelvis position track so the motion plays IN PLACE — the
 *     shape a gesture wants (pelvis stays at rest; layer additively over the SM
 *     base pose). Bone rotations are identical either way.
 *     'extract' also plays in place AND strips the character's turn out of the
 *     pelvis rotation, for a caller that drives the group itself (buildRootDrive).
 *     This is the mode that lets a locomotion clip blend out: with the travel and
 *     the turn both on the group, fading the clip fades a POSE and nothing else,
 *     so the character stops where and how it stands instead of sliding and
 *     un-rotating back to where it set off.
 * @returns {THREE.AnimationClip}
 */
export function buildBodyMotionClip(tracks, opts = {}) {
  const rootMode = opts.rootMode || 'travel'
  // The part rig first: when the splat is mounted, __AVATAR_SCENE__ does not exist.
  const rig = getLiveBodyRig()
  const scene = opts.scene || rig?.scene
    || (typeof window !== 'undefined' && window.__AVATAR_SCENE__)
  if (!scene) throw new Error('buildBodyMotionClip: avatar scene not ready')
  if (!tracks || !tracks.bones) throw new Error('buildBodyMotionClip: empty tracks')
  const name = opts.name || 'kimodo_generated'

  const fps = tracks.fps || 30
  const nFrames = tracks.frames || (Object.values(tracks.bones)[0].length / 4)
  const times = new Float32Array(nFrames)
  for (let f = 0; f < nFrames; f++) times[f] = f / fps

  // Root correction chain: the
  // rotation + scale the rig nests ABOVE the pelvis, accumulated avatar-root ->
  // pelvis.parent. Cancels the Armature's +90 X and 0.01 cm->m scale so the
  // clip's glTF-Y-up pelvis isn't tipped and world-meter travel maps to local.
  const rootCorr = new THREE.Quaternion()
  let rootInvScale = 1
  let pelvisRest = null
  const pelvis = scene.getObjectByName(ROOT_BONE)
  if (pelvis && pelvis.parent) {
    let scl = 1
    for (let n = pelvis.parent; n && n !== scene; n = n.parent) {
      rootCorr.premultiply(n.quaternion)
      scl *= (n.scale?.x ?? 1)
    }
    rootCorr.invert()
    rootInvScale = scl !== 0 ? 1 / scl : 1
    // The rig's OWN rest, when it captured one (PartRigDrive does, before its mixers
    // pose anything). Reading the live node instead reads whatever is posing the body
    // RIGHT NOW — on a splat that is the body blueprint, every frame, so the offset
    // between rest and the current pose would bake into the travel track as a permanent
    // displacement. The GLB path has no such capture and reads the node, as it did.
    const captured = rig?.scene === scene ? rig?.restPose?.get?.(ROOT_BONE)?.position : null
    pelvisRest = captured
      ? new THREE.Vector3(captured[0], captured[1], captured[2])
      : pelvis.position.clone()
  }
  // The rig's own hierarchy is only part of the story on a splat character, and on
  // myrav3 it is none of it — the chain above `pelvis` is identity. What actually turns
  // the character sits ABOVE the whole skeleton: the solved part fit maps the rig's
  // authored frame onto the trained one, and the bundle's world_rotation stands that up
  // at render (ogs/inference/rigRenderFrame.js composes the pair). Neither is a parent
  // node here, but both compose exactly like one, so the inverse belongs in this same
  // slot. Miss it and every generated clip renders in a frame turned 120 deg from the
  // one it was authored in — feet up, x and z along y and z.
  //
  // Folded into rootCorr rather than applied separately because the travel vector below
  // is rotated by the same quaternion: a walk whose facing and whose direction of travel
  // disagree is worse than one that is merely turned.
  const rigToWorld = opts.rigToWorld ?? rig?.rigToWorld ?? null
  if (rigToWorld) rootCorr.multiply(rigToWorld.clone().invert())
  // The SCALE half of that same pair, and it is missing from the hierarchy for exactly
  // the same reason. kimodo states the root in world METRES; the GLB avatar reads its
  // conversion off the Armature's 0.01 above the pelvis, but a splat part rig has an
  // IDENTITY chain there and is authored in centimetres — the cm→m lives in the solved
  // part fit, below the skeleton (ogs/inference/rigRenderFrame.js). Left out, the pelvis
  // track comes through 100x too small: the character still walks, because the travel
  // rides the avatar root in world metres, and a jump never leaves the ground.
  const rigToWorldScale = opts.rigToWorldScale ?? rig?.rigToWorldScale ?? null
  if (rigToWorldScale > 0) rootInvScale /= rigToWorldScale
  const applyRootCorr = Math.abs(rootCorr.w) < 0.99999

  // 'extract' mode: the character's TURN is taken out of the pelvis track and
  // handed to the avatar group (see buildRootDrive). Only the pelvis — child
  // bones are local to their parent, so rotating the pelvis rotates the body.
  // Removed in kimodo's own space, before rootCorr maps into the rig's.
  const yawSeries = rootMode === 'extract' ? rootYawSeries(tracks) : null
  const _yawInv = new THREE.Quaternion()
  const _up = new THREE.Vector3(0, 1, 0)

  const ktracks = []
  const _q = new THREE.Quaternion()
  let missing = 0
  for (const boneName in tracks.bones) {
    if (!scene.getObjectByName(boneName)) { missing++; continue }
    const src = tracks.bones[boneName]
    const quat = new Float32Array(nFrames * 4)
    for (let f = 0; f < nFrames; f++) {
      const o = f * 4
      _q.set(src[o], src[o + 1], src[o + 2], src[o + 3])
      if (boneName === ROOT_BONE && yawSeries) {
        _yawInv.setFromAxisAngle(_up, -yawSeries[f])
        _q.premultiply(_yawInv)
      }
      if (boneName === ROOT_BONE && applyRootCorr) _q.premultiply(rootCorr)
      quat[o] = _q.x; quat[o + 1] = _q.y; quat[o + 2] = _q.z; quat[o + 3] = _q.w
    }
    ktracks.push(new THREE.QuaternionKeyframeTrack(`${boneName}.quaternion`, times, quat))
  }
  if (missing) console.warn(`[bodyMotionClip] ${missing} track bone(s) not found on rig`)
  if (!ktracks.length) throw new Error('buildBodyMotionClip: no matching bones on rig')

  // Pelvis position (locomotion): rest + pelvis-local delta-from-frame0, so walk
  // /jump travel and leave the ground. Position tracks are stripped at rig load
  // (mixer never rewrites them), so a clip position track drives them directly.
  // Skipped entirely in 'lock' mode — an in-place gesture leaves the pelvis at
  // rest so it can layer additively without dragging the body across the floor.
  // 'extract' keeps a pelvis track too, but only the RESIDUAL — the raw root
  // minus the trajectory the group is given, plus the whole vertical component.
  // Without it the walk loses its hip sway and its bob entirely (the group runs
  // flat and straight, the body rides rigidly on top). With it, the sway stays
  // where the legs already compensate for it, and because the residual is zero
  // at both ends the pelvis starts and finishes at rest — so fading the clip out
  // moves the character by centimetres, not by the length of the walk.
  const traj = rootMode === 'extract' ? rootTrajectory(tracks) : null
  if ((rootMode === 'travel' || traj) && tracks.root && tracks.root.length >= 3 && pelvis && pelvisRest) {
    const r = tracks.root
    const r0x = r[0], r0y = r[1], r0z = r[2]
    const pos = new Float32Array(nFrames * 3)
    const _v = new THREE.Vector3()
    for (let f = 0; f < nFrames; f++) {
      if (traj) {
        _v.set(r[f * 3] - r0x - traj.sx[f], r[f * 3 + 1] - r0y, r[f * 3 + 2] - r0z - traj.sz[f])
      } else
      _v.set(r[f * 3] - r0x, r[f * 3 + 1] - r0y, r[f * 3 + 2] - r0z)
      _v.applyQuaternion(rootCorr).multiplyScalar(rootInvScale)
      pos[f * 3] = pelvisRest.x + _v.x
      pos[f * 3 + 1] = pelvisRest.y + _v.y
      pos[f * 3 + 2] = pelvisRest.z + _v.z
    }
    ktracks.push(new THREE.VectorKeyframeTrack(`${ROOT_BONE}.position`, times, pos))
  }

  const built = new THREE.AnimationClip(name, nFrames / fps, ktracks)
  // What the pelvis track MEANS, for a rig that prunes by role (pruneBodyClipTracks).
  // In 'extract' mode it is the residual sway plus the vertical — the bob — and the
  // travel is already off it, so a rig must KEEP it. In 'travel' mode the same track
  // carries the whole walk, and a rig whose root travels separately must strip it or the
  // character moves twice. Stamped on the clip because only this function knows which.
  built.userData = { ...built.userData, aosGeneratedMotion: { keepRootMotion: rootMode === 'extract' } }
  return built
}

// Shared canonical name for the tracks->clip conversion. Both callers use it:
// the anchor/navmesh path with rootMode:'travel', an in-place gesture with
// rootMode:'lock'. Kept as an alias so the existing travel callsite is unchanged.
export { buildBodyMotionClip as kimodoTracksToClip }

/** The same one-shot on a rig that takes clip weights: the avatar root carries the travel.
 *  Notes: ~/comments/aos-threejs-poc/generatedRootMotion_createRootMotion_render_loop_clock.md */
function playGeneratedClipOnRig(clip, opts, rig) {
  rig.register(clip.name, clip, { additive: false })
  const now = Date.now()
  const travel = opts.commitRootDelta
  const yaw = opts.commitYawDelta ?? 0
  const group = resolveAvatarTransformTarget()
  const motion = group && (hasTravel(travel) || yaw || opts.rootYawPath)
    ? createRootMotion({
        group, travel, yaw, frames: opts.rootFrames ?? 0,
        rootPath: opts.rootPath ?? null, yawPath: opts.rootYawPath ?? null,
      })
    : null
  if (!motion && hasTravel(travel)) {
    console.info(`[bodyMotionClip] '${clip.name}' plays in place — no avatar root to carry `
      + `its ${Math.hypot(travel[0], travel[1]).toFixed(2)}m of travel`)
  }
  // The same envelope the GLB path gets from applyWeights: the clip fades UP over the
  // graph's idle and back DOWN into it, instead of cutting in and out. The part rig
  // reads this in multipartBodyDrive.
  //
  // blendOut is unconditional here, where the GLB path has to gate it on an EXTRACTED
  // root (`moves ? 0 : 300`): this rig binds bone ROTATIONS only — poseTracksOnly strips
  // the pelvis track and the travel rides the avatar root — so a fading clip cannot drag
  // the character back the way one still carrying pelvis.position would.
  const blendInMs = opts.blendInMs ?? 200
  const blendOutMs = opts.blendOutMs ?? 300
  window.__DIRECT_PLAY__ = {
    clipName: clip.name,
    startTime: now,
    // followAction: the part rig ends the hold on the clip's own clock; this is the wedge.
    expiresAt: now + clip.duration * 1000 + blendOutMs + 50,
    followAction: true,
    blendInMs,
    blendOutMs,
    rootMotion: motion, // placed by the render loop at the clip's progress
    onExpire: motion ? () => motion.finish() : undefined,
  }
  return clip.name
}

/** Fire a still-active generated hold's onExpire before replacing it, or its root landing is lost. */
function releasePriorHold() {
  const prevDP = typeof window !== 'undefined' ? window.__DIRECT_PLAY__ : null
  if (!prevDP || typeof prevDP.onExpire !== 'function') return
  window.__DIRECT_PLAY__ = null
  try { prevDP.onExpire() } catch (err) { console.warn('[bodyMotionClip] prior onExpire threw:', err) }
}

/**
 * Compute the [dx, dz] world-meter XZ root travel (frame-last minus frame-0)
 * from a kimodo tracks payload's `root` track — the `commitRootDelta` shape
 * `playGeneratedClip` expects. Pulled out as its own export so every
 * travel-mode caller (bodyMotionNav.js's playTracks, EventRuntime's
 * performance-classified gesture branch) shares one formula instead of each
 * re-deriving it against the same raw array layout.
 * @param {object} tracks kimodo tracks payload — { root?:[x,y,z,...], frames? }
 * @returns {[number, number]|null} [dx, dz], or null with fewer than 2 frames
 */
/**
 * Smooth a per-frame delta series: a centred moving average, re-zeroed at the
 * start and ramped so the last frame is preserved exactly.
 *
 * Shared by the yaw and the XZ trajectory because they need the same three
 * properties, and getting any one of them wrong is a visible artefact:
 * frame 0 must stay 0 (the pose lock and heading ramp anchor there), the last
 * frame must stay exact (the group has to finish where and how the walk did),
 * and everything between must lose the per-step oscillation.
 */
function smoothDeltaSeries(raw, smoothFrames) {
  const nf = raw.length
  const half = Math.max(0, Math.floor(smoothFrames / 2))
  if (half === 0 || nf < 3) return raw
  const out = new Float32Array(nf)
  for (let f = 0; f < nf; f++) {
    // Clamped at the ends rather than shrinking the window: a shrinking window
    // re-admits the sway exactly at the frames that must be clean.
    let sum = 0
    for (let k = -half; k <= half; k++) sum += raw[Math.min(nf - 1, Math.max(0, f + k))]
    out[f] = sum / (half * 2 + 1)
  }
  const s0 = out[0]
  for (let f = 0; f < nf; f++) out[f] -= s0
  const bias = raw[nf - 1] - out[nf - 1]
  if (Math.abs(bias) > 1e-9) for (let f = 0; f < nf; f++) out[f] += bias * (f / (nf - 1))
  return out
}

/**
 * The character's TRAJECTORY: kimodo's root travel with the walk cycle's lateral
 * sway averaged out, as XZ deltas from frame 0.
 *
 * The root track is the hips, so it weaves a few centimetres each step. Driving
 * the group with the whole of it moves the entire rig — planted feet included —
 * and the walk reads as jumpy. The trajectory goes to the group; the residual
 * (raw minus this) stays in the clip's pelvis track, where the legs already
 * compensate for it. Both series are zero at frame 0 and exact at the last, so
 * the pelvis starts and ends at rest and the group covers the full distance.
 *
 * @returns {{sx:Float32Array, sz:Float32Array}|null}
 */
export function rootTrajectory(tracks, { smoothFrames = DEFAULT_YAW_SMOOTH_FRAMES } = {}) {
  const r = tracks?.root
  const nf = tracks?.frames || (r ? Math.floor(r.length / 3) : 0)
  if (!r || nf < 2) return null
  const dx = new Float32Array(nf)
  const dz = new Float32Array(nf)
  for (let f = 0; f < nf; f++) {
    dx[f] = r[f * 3] - r[0]
    dz[f] = r[f * 3 + 2] - r[2]
  }
  return { sx: smoothDeltaSeries(dx, smoothFrames), sz: smoothDeltaSeries(dz, smoothFrames) }
}

/**
 * Frames averaged when separating a walk's HEADING from its hip sway. 15 at
 * 30 fps is half a second — wider than a step (~0.55 s at a walk), so a full
 * oscillation averages out and only the turn survives.
 */
export const DEFAULT_YAW_SMOOTH_FRAMES = 15

/**
 * The character's turn, per frame, relative to frame 0 — radians about Y, read
 * off the pelvis quaternions of a kimodo payload.
 *
 * The basis of ROOT MOTION EXTRACTION: this yaw is removed from the clip's
 * pelvis track and driven onto the avatar group instead, so the clip holds a
 * pose and the group holds where the character is and which way it faces. Which
 * is what makes a tail blend possible at all — while the turn lives in the
 * bones, fading the clip out fades the turn away too, and the character
 * un-rotates as it stops.
 *
 * @param {object} tracks kimodo /generate payload
 * @returns {Float32Array|null} one entry per frame, [0] === 0
 */
export function rootYawSeries(tracks, { smoothFrames = DEFAULT_YAW_SMOOTH_FRAMES } = {}) {
  const q = tracks?.bones?.pelvis
  const nf = tracks?.frames || (q ? Math.floor(q.length / 4) : 0)
  if (!q || nf < 1) return null
  const yawOf = (i) => {
    const x = q[i * 4], y = q[i * 4 + 1], z = q[i * 4 + 2], w = q[i * 4 + 3]
    return Math.atan2(2 * (w * y + x * z), 1 - 2 * (y * y + z * z))
  }
  const base = yawOf(0)
  const out = new Float32Array(nf)
  // Unwrapped: a walk that turns past +/-pi must keep accumulating rather than
  // jumping a full turn, or the group would spin the wrong way round mid-clip.
  let prev = 0
  for (let f = 0; f < nf; f++) {
    let d = yawOf(f) - base
    while (d - prev > Math.PI) d -= Math.PI * 2
    while (d - prev < -Math.PI) d += Math.PI * 2
    out[f] = d
    prev = d
  }

  // LOW-PASS. The pelvis yaw is not just where the character is heading — it
  // also carries the walk cycle's hip sway, one oscillation per step. Extracting
  // that onto the group swings the WHOLE character, feet included, instead of
  // the hips rotating over planted feet, which reads as the walk being jumpy.
  return smoothDeltaSeries(out, smoothFrames)
}

/**
 * The per-frame group drive for an extracted clip: where the character is and
 * which way it faces, relative to wherever the group stood when the clip began.
 *
 * XZ travel comes from kimodo's own `root` track; the turn from rootYawSeries.
 * Both are deltas from frame 0, so the caller adds them to the group's pose at
 * play time and nothing has to be committed when the clip ends — the group is
 * continuously correct, and there is no boundary left to be discontinuous at.
 *
 * @param {object} tracks kimodo /generate payload
 * @returns {{fps:number, frames:number, dx:Float32Array, dz:Float32Array, dyaw:Float32Array}|null}
 */
export function buildRootDrive(tracks) {
  const r = tracks?.root
  const nf = tracks?.frames || (r ? Math.floor(r.length / 3) : 0)
  if (!r || nf < 2) return null
  const traj = rootTrajectory(tracks)
  return {
    fps: tracks.fps || 30,
    frames: nf,
    dx: traj.sx,
    dz: traj.sz,
    dyaw: rootYawSeries(tracks) || new Float32Array(nf),
  }
}

/**
 * How far the clip turns the character, in radians about Y — the rotational
 * sibling of computeRootDelta.
 *
 * It exists for the same reason. The clip rotates the character through its BONE
 * tracks, inside the avatar group, so when the clip ends and the base pose
 * resumes, the character snaps back to the group's own yaw — a walk that rounds
 * a corner ends facing the way it set off, and the idle that follows points
 * somewhere the animation never did. Committing this to the group at the instant
 * the pose is dropped keeps the facing, exactly as commitRootDelta keeps the
 * position.
 *
 * @param {object} tracks kimodo /generate payload
 * @returns {number} radians, wrapped to (-pi, pi]
 */
export function computeYawDelta(tracks) {
  const q = tracks?.bones?.pelvis
  const nf = tracks?.frames || (q ? Math.floor(q.length / 4) : 0)
  if (!q || nf < 2) return 0
  // Yaw about Y from a quaternion (x,y,z,w). The pelvis track is the character's
  // own rotation, so its net change over the clip is how far the walk turned.
  const yawOf = (i) => {
    const x = q[i * 4], y = q[i * 4 + 1], z = q[i * 4 + 2], w = q[i * 4 + 3]
    return Math.atan2(2 * (w * y + x * z), 1 - 2 * (y * y + z * z))
  }
  let d = (yawOf(nf - 1) - yawOf(0)) % (Math.PI * 2)
  if (d > Math.PI) d -= Math.PI * 2
  if (d <= -Math.PI) d += Math.PI * 2
  return d
}

export function computeRootDelta(tracks) {
  const r = tracks?.root || []
  const nf = tracks?.frames || Math.floor(r.length / 3)
  return nf >= 2 ? [r[(nf - 1) * 3] - r[0], r[(nf - 1) * 3 + 2] - r[2]] : null
}

/**
 * Register a built clip as a mixer action in window.__AVATAR_ACTIONS__ and play
 * it once via the __DIRECT_PLAY__ override (the render loop's applyWeights takes
 * exclusive control, blends in/out, and calls play()). Returns the clip name.
 * @param {THREE.AnimationClip} clip
 * @param {object} [opts] { blendInMs, blendOutMs, keepBase, commitRootDelta }
 *   commitYawDelta: radians about Y — applied to the group alongside the
 *   translation, so the character keeps the facing the clip ended on.
 *   commitRootDelta: [dx, dz] world meters — applied to the avatar group when
 *   the clip finishes, so locomotion ENDS at the travelled position instead of
 *   snapping back to the pre-clip spot (the base pose lives at the old group
 *   transform). Pass null/omit for in-place motion.
 */
export function playGeneratedClip(clip, opts = {}) {
  const mixer = typeof window !== 'undefined' && window.__AVATAR_MIXER__
  const actions = typeof window !== 'undefined' && window.__AVATAR_ACTIONS__
  if (!mixer || !actions) {
    const rig = getLiveBodyRig()
    if (!rig?.register) throw new Error('playGeneratedClip: avatar mixer/actions not ready')
    releasePriorHold()
    return playGeneratedClipOnRig(clip, opts, rig)
  }

  const name = clip.name
  releasePriorHold()

  // Rebuild cleanly if re-generating under the same name.
  const prev = actions[name]
  if (prev) {
    try { prev.stop() } catch { /* ignore */ }
    try { mixer.uncacheAction(prev.getClip(), prev.getRoot()) } catch { /* ignore */ }
    try { mixer.uncacheClip(prev.getClip()) } catch { /* ignore */ }
  }

  const action = mixer.clipAction(clip)
  action.setLoop(THREE.LoopOnce, 1)
  action.clampWhenFinished = true
  action.reset()
  // Start it now so it is running from frame 0 (isRunning() true immediately) —
  // the render loop's applyWeights then just manages the blend-in/out weight
  // envelope via __DIRECT_PLAY__ (it no-ops the play() since we're already running).
  action.play()
  actions[name] = action

  // Root-motion commit: when the direct-play hold releases (the clip reached
  // its end under followAction, or the wall-clock wedge guard fired), move the
  // avatar group by the clip's world travel so the resuming base pose continues
  // FROM the destination instead of snapping back. Done via the hold's onExpire
  // hook rather than THREE's 'finished' event — the followAction expiry can
  // release the clip a frame before the mixer would dispatch 'finished'.
  //
  // Two coordinate spaces meet here and MUST stay consistent:
  //   * the pelvis.position track (built above) carries the travel in
  //     pelvis-LOCAL space — rootCorr + rootInvScale applied — so the mixer
  //     drives it correctly through the rig's nested transforms;
  //   * `delta` is the SAME travel in RAW world meters (kimodo root is already
  //     world-space; __AVATAR_GROUP__ is a world-space, axis-aligned wrapper),
  //     applied directly to the group.
  // They reconcile because action.stop() (below) removes the pelvis-track
  // contribution in the same frame the group shifts: net world displacement =
  // the group's raw delta, with no double-offset from the held final pose.
  // Root drive: the group is moved and turned every frame from these deltas, so
  // an extracted clip needs no commit at all. Base pose captured HERE, at play
  // time, not at generate time — the wizard can relocate the avatar in between.
  const drive = opts.rootDrive || null
  const rootDrive = drive && typeof window !== 'undefined' && window.__AVATAR_GROUP__
    ? {
        ...drive,
        baseX: window.__AVATAR_GROUP__.position.x,
        baseZ: window.__AVATAR_GROUP__.position.z,
        baseYaw: window.__AVATAR_GROUP__.rotation.y,
      }
    : null

  const delta = rootDrive ? null : opts.commitRootDelta
  const yaw = rootDrive ? 0 : (opts.commitYawDelta || 0)
  const moves = delta && (Math.abs(delta[0]) > 1e-3 || Math.abs(delta[1]) > 1e-3)
  const turns = Math.abs(yaw) > 1e-3
  const commit = (moves || turns)
    ? () => {
        const g = window.__AVATAR_GROUP__
        if (g && delta) { g.position.x += delta[0]; g.position.z += delta[1] }
        // Keep the facing the walk ended on. Applied in the same frame the pose
        // is dropped, so there is no frame in which the two disagree.
        if (g && turns) g.rotation.y += yaw
        // Drop the clip pose in the same frame the group shifts — the held
        // final pose already stands at the destination and would be
        // double-offset otherwise.
        action.stop()
      }
    : null

  const blendInMs = opts.blendInMs ?? 200
  // With a root commit the tail must NOT fade back toward the base pose at the
  // old position mid-walk — cut over at the end instead (the commit handles
  // position continuity; generated walks end near-standing so the pose pop is
  // small).
  // A driven clip CAN fade out: its travel and turn live on the group, so fading
  // it fades a pose. Only a clip that still carries travel in pelvis.position
  // must cut — fading that drags the character back the whole distance.
  const blendOutMs = opts.blendOutMs ?? (moves ? 0 : 300)
  const now = Date.now()
  window.__DIRECT_PLAY__ = {
    clipName: name,
    startTime: now,
    // Under followAction this is NOT the real deadline — applyWeights expires on
    // the action's actual progress and treats expiresAt only as a wedge guard
    // (padded +15s there) so a stalled/clamped frame loop (GPU busy right after
    // generation) can't cut the clip short. For a non-followAction one-shot it
    // would be the true wall-clock deadline.
    expiresAt: now + clip.duration * 1000 + blendOutMs + 50,
    followAction: true,
    onExpire: commit,
    blendInMs,
    blendOutMs,
    rootDrive,
    keepBase: !!opts.keepBase,
  }
  return name
}
